import { Audiometer } from "services/types/audiometers.type";

import EnumDataSource from "../utils/enum-data-source.util";
import SortedEnumDataSource from "../utils/sorted-enum-data-source-util";

export const DEFAULT_RELIABILITY_TYPE = 1;
const reliabilityTypes = {
  [DEFAULT_RELIABILITY_TYPE]: "Excellent",
  "2": "Good",
  "3": "Fair",
  "4": "Poor"
};

export const DEFAULT_TEST_METHOD = 1;

const testMethodTypes = {
  [DEFAULT_TEST_METHOD]: "Conventional",
  "2": "BOA",
  "3": "CPA",
  "4": "VRA",
  "5": "CPA/Conventional",
  "6": "VRA/BOA",
  "7": "VRA/Conventional",
  "8": "VRA/CPA"
};
export const DEFAULT_TRANSDUCER_TYPE = 2;

const transducerTypes = {
  "1": "Headphones",
  [DEFAULT_TRANSDUCER_TYPE]: "Inserts",
  "3": "Soundfield",
  "4": "Speakers",
  "5": "Inserts (pediatric)",
  "6": "Inserts (adult)",
  "7": "Inserts (jumbo)",
  "8": "Supra-aural",
  "9": "Circumaural"
};
const acousticReflexDataPoints = {
  Y: "Yes",
  N: "No",
  A: "Absent",
  CNT: "CNT",
  DNT: "DNT",
  CNS: "CNS"
};

const acousticReflexDecayDataPoints = {
  P: "Positive",
  N: "Negative",
  CNT: "CNT",
  DNT: "DNT",
  CNS: "CNS"
};

export const SOUNDFIELD_EARPHONES_METHOD_TYPE = "S";
export const SPEECH_IN_NOICE_EARPHONES_METHOD_TYPE = "E";

const speechInNoiseMethodTypes = {
  [SOUNDFIELD_EARPHONES_METHOD_TYPE]: "Soundfield",
  [SPEECH_IN_NOICE_EARPHONES_METHOD_TYPE]: "Earphones"
};

const degreeOfSnrLossTypes = {
  "1": "Normal",
  "2": "Mild",
  "3": "Moderate",
  "4": "Severe",
  "5": "DNT",
  "6": "CNT"
};

const tympanometryTypes = {
  A: "A",
  AD: "A[d]",
  AS: "A[s]",
  B: "B",
  C: "C",
  CNS: "CNS",
  CNT: "CNT",
  DNT: "DNT",
  FLAT: "Flat",
  WNL: "WNL"
};

const probeTones = {
  "226": "226 Hz",
  "678": "678 Hz",
  "1000": "1000 Hz"
};

export const SPEECH_AUDIOMETRY_TYPE_SRT = 1;
export const SPEECH_AUDIOMETRY_TYPE_SAT = 2;

const speechAudiometryTypes = {
  [SPEECH_AUDIOMETRY_TYPE_SRT]: "SRT",
  [SPEECH_AUDIOMETRY_TYPE_SAT]: "SAT"
};

export const WORD_RECOGNITION_PRESENTATION_TYPE_MLV = 1;
export const WORD_RECOGNITION_PRESENTATION_TYPE_RECORDED = 2;
export const WORD_RECOGNITION_PRESENTATION_TYPE_SPANISH_MLV = 3;
export const WORD_RECOGNITION_PRESENTATION_TYPE_SPANISH_RECORDED = 4;
const wordRecognitionPresentationTypes = {
  [WORD_RECOGNITION_PRESENTATION_TYPE_MLV]: "MLV",
  [WORD_RECOGNITION_PRESENTATION_TYPE_RECORDED]: "Recorded",
  [WORD_RECOGNITION_PRESENTATION_TYPE_SPANISH_MLV]: "Spanish - MLV",
  [WORD_RECOGNITION_PRESENTATION_TYPE_SPANISH_RECORDED]: "Spanish - Recorded"
};

const wordListDifficultyTypes = {
  "1": "Very easy",
  "2": "Easy",
  "3": "Normal",
  "4": "Hard",
  "5": "Very hard"
};

export const RELIABILITY_TYPES = new EnumDataSource(reliabilityTypes);

export const TEST_METHOD_TYPES = new EnumDataSource(testMethodTypes);
export const TRANSDUCER_TYPES = new EnumDataSource(transducerTypes);
export const ACOUSTIC_REFLEX_DATA_POINTS = new EnumDataSource(
  acousticReflexDataPoints
);

export const ACOUSTIC_REFLEX_DECAY_DATA_POINTS = new EnumDataSource(
  acousticReflexDecayDataPoints
);
export const SPEECH_IN_NOISE_METHOD_TYPES = new EnumDataSource(
  speechInNoiseMethodTypes
);

export const DEGREE_OF_SNR_LOSS_TYPES = new EnumDataSource(
  degreeOfSnrLossTypes
);
export const TYMPANOMETRY_TYPES = new EnumDataSource(tympanometryTypes);

export const PROBE_TONES = new EnumDataSource(probeTones);

export const SPEECH_AUDIOMETRY_TYPES = new EnumDataSource(
  speechAudiometryTypes
);

export const WORD_RECOGNITION_PRESENTATION_TYPES = new EnumDataSource(
  wordRecognitionPresentationTypes
);

export const WORD_LIST_DIFFICULTY_TYPES = new EnumDataSource(
  wordListDifficultyTypes
);

export const DEFAULT_SPEECH_AUDIOMETRY_TYPE = 1;
export const DEFAULT_AUDIOMETER_SELECTION: Audiometer = {
  id: 2,
  name: "Interacoustics AC-40"
};
export const DEFAULT_PROBE_TONE = 226;

export const ACOUSTIC_REFLEX_COLUMNS = [
  "Threshold in DBHL",
  "500 Hz",
  "1000 Hz",
  "2000 Hz",
  "4000 Hz"
];
// Bulk-fill choices for the per-ear "Set all" menu (SPA-6371). Yes/No are
// deliberately excluded: a present reflex is recorded as its threshold, so
// bulk-setting an ear only makes clinical sense for the not-elicited states.
export const ACOUSTIC_REFLEX_SET_ALL_CODES = ["A", "CNT", "DNT", "CNS"];
export const ACOUSTIC_REFLEX_DECAY_COLUMNS = [
  "Threshold in DBHL",
  "500 Hz",
  "1000 Hz"
];

export const REPORT_PDF_WIDTH = 2169;

export const HEARING_NOT_TESTED_LOSS_LEVEL = 0;
export const HEARING_NORMAL_LOSS_LEVEL = 1;
export const HEARING_MILD_LOSS_LEVEL = 2;
export const HEARING_MODERATE_LOSS_LEVEL = 3;
export const HEARING_MODERATELY_SEVERE_LOSS_LEVEL = 4;
export const HEARING_SEVERE_LOSS_LEVEL = 5;
export const HEARING_PROFOUND_LOSS_LEVEL = 6;
export const HEARING_UNAIDABLE_LOSS_LEVEL = 7;
export const HEARING_SLIGHT_LOSS_LEVEL = 8;
export const HEARING_NORMAL_MILD_LOSS_LEVEL = 9;
export const HEARING_NORMAL_MODERATE_LOSS_LEVEL = 10;
export const HEARING_NORMAL_MODERATELY_SEVERE_LOSS_LEVEL = 11;
export const HEARING_NORMAL_SEVERE_LOSS_LEVEL = 12;
export const HEARING_NORMAL_PROFOUND_LOSS_LEVEL = 13;
export const HEARING_MILD_NORMAL_LOSS_LEVEL = 14;
export const HEARING_MILD_MODERATE_LOSS_LEVEL = 15;
export const HEARING_MILD_MODERATELY_SEVERE_LOSS_LEVEL = 16;
export const HEARING_MILD_SEVERE_LOSS_LEVEL = 17;
export const HEARING_MILD_PROFOUND_LOSS_LEVEL = 18;
export const HEARING_MODERATE_NORMAL_LOSS_LEVEL = 19;
export const HEARING_MODERATE_MILD_LOSS_LEVEL = 20;
export const HEARING_MODERATE_MODERATELY_SEVERE_LOSS_LEVEL = 21;
export const HEARING_MODERATE_SEVERE_LOSS_LEVEL = 22;
export const HEARING_MODERATE_PROFOUND_LOSS_LEVEL = 23;
export const HEARING_MODERATELY_SEVERE_NORMAL_LOSS_LEVEL = 24;
export const HEARING_MODERATELY_SEVERE_MILD_LOSS_LEVEL = 25;
export const HEARING_MODERATELY_SEVERE_MODERATE_LOSS_LEVEL = 26;
export const HEARING_MODERATELY_SEVERE_SEVERE_LOSS_LEVEL = 27;
export const HEARING_MODERATELY_SEVERE_PROFOUND_LOSS_LEVEL = 28;
export const HEARING_SEVERE_NORMAL_LOSS_LEVEL = 29;
export const HEARING_SEVERE_MILD_LOSS_LEVEL = 30;
export const HEARING_SEVERE_MODERATE_LOSS_LEVEL = 31;
export const HEARING_SEVERE_MODERATELY_SEVERE_LOSS_LEVEL = 32;
export const HEARING_SEVERE_PROFOUND_LOSS_LEVEL = 33;
export const HEARING_PROFOUND_NORMAL_LOSS_LEVEL = 34;
export const HEARING_PROFOUND_MILD_LOSS_LEVEL = 35;
export const HEARING_PROFOUND_MODERATE_LOSS_LEVEL = 36;
export const HEARING_PROFOUND_MODERATELY_SEVERE_LOSS_LEVEL = 37;
export const HEARING_PROFOUND_SEVERE_LOSS_LEVEL = 38;

const hearingLossLevel = new Map([
  [HEARING_NOT_TESTED_LOSS_LEVEL, "Not Tested"],
  [HEARING_NORMAL_LOSS_LEVEL, "Normal"],
  [HEARING_SLIGHT_LOSS_LEVEL, "Slight"],
  [HEARING_MILD_LOSS_LEVEL, "Mild"],
  [HEARING_MODERATE_LOSS_LEVEL, "Moderate"],
  [HEARING_MODERATELY_SEVERE_LOSS_LEVEL, "Moderately Severe"],
  [HEARING_SEVERE_LOSS_LEVEL, "Severe"],
  [HEARING_PROFOUND_LOSS_LEVEL, "Profound"],
  [HEARING_UNAIDABLE_LOSS_LEVEL, "Unaidable"],
  [HEARING_NORMAL_MILD_LOSS_LEVEL, "Normal-Mild"],
  [HEARING_NORMAL_MODERATE_LOSS_LEVEL, "Normal-Moderate"],
  [HEARING_NORMAL_MODERATELY_SEVERE_LOSS_LEVEL, "Normal-Moderately Severe"],
  [HEARING_NORMAL_SEVERE_LOSS_LEVEL, "Normal-Severe"],
  [HEARING_NORMAL_PROFOUND_LOSS_LEVEL, "Normal-Profound"],
  [HEARING_MILD_NORMAL_LOSS_LEVEL, "Mild-Normal"],
  [HEARING_MILD_MODERATE_LOSS_LEVEL, "Mild-Moderate"],
  [HEARING_MILD_MODERATELY_SEVERE_LOSS_LEVEL, "Mild-Moderately Severe"],
  [HEARING_MILD_SEVERE_LOSS_LEVEL, "Mild-Severe"],
  [HEARING_MILD_PROFOUND_LOSS_LEVEL, "Mild-Profound"],
  [HEARING_MODERATE_NORMAL_LOSS_LEVEL, "Moderate-Normal"],
  [HEARING_MODERATE_MILD_LOSS_LEVEL, "Moderate-Mild"],
  [HEARING_MODERATE_MODERATELY_SEVERE_LOSS_LEVEL, "Moderate-Moderately Severe"],
  [HEARING_MODERATE_SEVERE_LOSS_LEVEL, "Moderate-Severe"],
  [HEARING_MODERATE_PROFOUND_LOSS_LEVEL, "Moderate-Profound"],
  [HEARING_MODERATELY_SEVERE_NORMAL_LOSS_LEVEL, "Moderately Severe-Normal"],
  [HEARING_MODERATELY_SEVERE_MILD_LOSS_LEVEL, "Moderately Severe-Mild"],
  [HEARING_MODERATELY_SEVERE_MODERATE_LOSS_LEVEL, "Moderately Severe-Moderate"],
  [HEARING_MODERATELY_SEVERE_SEVERE_LOSS_LEVEL, "Moderately Severe-Severe"],
  [HEARING_MODERATELY_SEVERE_PROFOUND_LOSS_LEVEL, "Moderately Severe-Profound"],
  [HEARING_SEVERE_NORMAL_LOSS_LEVEL, "Severe-Normal"],
  [HEARING_SEVERE_MILD_LOSS_LEVEL, "Severe-Mild"],
  [HEARING_SEVERE_MODERATE_LOSS_LEVEL, "Severe-Moderate"],
  [HEARING_SEVERE_MODERATELY_SEVERE_LOSS_LEVEL, "Severe-Moderately Severe"],
  [HEARING_SEVERE_PROFOUND_LOSS_LEVEL, "Severe-Profound"],
  [HEARING_PROFOUND_NORMAL_LOSS_LEVEL, "Profound-Normal"],
  [HEARING_PROFOUND_MILD_LOSS_LEVEL, "Profound-Mild"],
  [HEARING_PROFOUND_MODERATE_LOSS_LEVEL, "Profound-Moderate"],
  [HEARING_PROFOUND_MODERATELY_SEVERE_LOSS_LEVEL, "Profound-Moderately Severe"],
  [HEARING_PROFOUND_SEVERE_LOSS_LEVEL, "Profound-Severe"]
]);

export const HEARING_LOSS_LEVEL = new SortedEnumDataSource(hearingLossLevel);

export const HEARING_LOSS_NOT_APPLICABLE_TYPE = 1;
export const HEARING_LOSS_SENSORY_TYPE = 2;
export const HEARING_LOSS_NEURAL_TYPE = 3;
export const HEARING_LOSS_CONDUCTIVE_TYPE = 4;
export const HEARING_LOSS_MIXED_TYPE = 5;
export const HEARING_LOSS_SENSORINEURAL_TYPE = 6;

const hearingLossType = {
  [HEARING_LOSS_NOT_APPLICABLE_TYPE]: "Not applicable",
  [HEARING_LOSS_SENSORY_TYPE]: "Sensory",
  [HEARING_LOSS_NEURAL_TYPE]: "Neural",
  [HEARING_LOSS_CONDUCTIVE_TYPE]: "Conductive",
  [HEARING_LOSS_MIXED_TYPE]: "Mixed",
  [HEARING_LOSS_SENSORINEURAL_TYPE]: "Sensorineural"
};

export const HEARING_LOSS_TYPE = new EnumDataSource(hearingLossType);

export const HEARING_LOSS_NOT_APPLICABLE_SHAPE = 1;
export const HEARING_LOSS_FLAT_SHAPE = 2;
export const HEARING_LOSS_REVERSE_SLOPE_SHAPE = 3;
export const HEARING_LOSS_COOKIE_BITE_SHAPE = 4;
export const HEARING_LOSS_PRECIPITOUS_SHAPE = 5;
export const HEARING_LOSS_SLOPING_SHAPE = 6;
export const HEARING_LOSS_HIGH_FREQUENCY = 7;
export const HEARING_LOSS_MID_TO_HIGH_FREQUENCY = 8;
export const HEARING_LOSS_NOTCHED = 9;
export const HEARING_LOSS_REVERSE_COOKIE_BITE = 10;

const hearingLossShape = {
  [HEARING_LOSS_NOT_APPLICABLE_SHAPE]: "Not applicable",
  [HEARING_LOSS_FLAT_SHAPE]: "Flat",
  [HEARING_LOSS_REVERSE_SLOPE_SHAPE]: "Reverse slope",
  [HEARING_LOSS_COOKIE_BITE_SHAPE]: "Cookie bite",
  [HEARING_LOSS_PRECIPITOUS_SHAPE]: "Precipitous",
  [HEARING_LOSS_SLOPING_SHAPE]: "Sloping",
  [HEARING_LOSS_MID_TO_HIGH_FREQUENCY]: "Mid - High Frequency",
  [HEARING_LOSS_HIGH_FREQUENCY]: "High Frequency",
  [HEARING_LOSS_NOTCHED]: "Notched",
  [HEARING_LOSS_REVERSE_COOKIE_BITE]: "Reverse cookie bite"
};

export const HEARING_LOSS_SHAPE = new EnumDataSource(hearingLossShape);

export const REPORT_TYPE_AUDIOLOGY = 1;
export const REPORT_TYPE_TEXT = 2;
/**
 * A Noah session that carried only Real Ear Measurement (a fitting visit): no audiogram, no
 * diagnosis, no summary. Mirrors `ReportType.REM` on the backend (SN-19637).
 */
export const REPORT_TYPE_REM = 3;
/**
 * A Noah session whose only verification is the hearing aid on a coupler in a test box (Audioscan
 * Speechmap test-box runs, ANSI S3.22): what clinics call EAE. Not REM — no probe microphone was in
 * the ear. Mirrors `ReportType.TEST_BOX` on the backend (DEV-3230).
 */
export const REPORT_TYPE_TEST_BOX = 4;

const reportTypes = {
  [REPORT_TYPE_AUDIOLOGY]: "Audiology",
  [REPORT_TYPE_TEXT]: "Text",
  [REPORT_TYPE_REM]: "Real Ear Measurement",
  [REPORT_TYPE_TEST_BOX]: "Hearing Aid Test Box (EAE)"
};

export const REPORT_TYPES = new EnumDataSource(reportTypes);

/**
 * Report types that document a hearing aid (a fitting verified on the ear, or the aid checked in a
 * test box) rather than a hearing test. Only Noah sync creates them, and they have no diagnosis, no
 * test configuration and no audiology layout options. Mirrors the backend's
 * `NO_AUTO_NARRATIVE_REPORT_TYPES`.
 */
const HEARING_AID_VERIFICATION_REPORT_TYPES: ReadonlySet<number> = new Set([
  REPORT_TYPE_REM,
  REPORT_TYPE_TEST_BOX
]);

export const isHearingAidVerificationReportType = (
  type: number | null | undefined
): boolean => type != null && HEARING_AID_VERIFICATION_REPORT_TYPES.has(type);

/** Types the new-report form never offers: Noah sync is their only writer. */
export const NOAH_ONLY_REPORT_TYPES = Array.from(
  HEARING_AID_VERIFICATION_REPORT_TYPES
);

/**
 * Whether the backend drafts a summary with AI for this report type. A test box check has nothing to
 * narrate, and `/reports/<id>/complete` returns 400 for it. A REM report's summary can still be drafted
 * on demand.
 */
export const reportTypeHasAiSummary = (
  type: number | null | undefined
): boolean => type !== REPORT_TYPE_TEST_BOX;

export const AUDIOGRAM_PURE_TONE_ABSENT = "A";
export const AUDIOGRAM_PURE_TONE_CNT = "CNT";
export const AUDIOGRAM_PURE_TONE_DNT = "DNT";
export const AUDIOGRAM_PURE_TONE_CNS = "CNS";

export const AUDIOGRAM_MASKED_INDICATOR = "M";

export const AUDIOGRAM_OTOACOUSTIC_PRES = "PRES";
export const AUDIOGRAM_OTOACOUSTIC_RED = "RED";

export const SEGMENTING_VALUES = [
  AUDIOGRAM_PURE_TONE_ABSENT,
  AUDIOGRAM_PURE_TONE_CNT,
  AUDIOGRAM_PURE_TONE_DNT,
  AUDIOGRAM_PURE_TONE_CNS
];

const audiogramPureToneValues = {
  [AUDIOGRAM_PURE_TONE_ABSENT]: "NR",
  [AUDIOGRAM_PURE_TONE_CNT]: "CNT",
  [AUDIOGRAM_PURE_TONE_DNT]: "DNT",
  [AUDIOGRAM_PURE_TONE_CNS]: "CNS"
};

const otoacousticValues = {
  ...audiogramPureToneValues,
  [AUDIOGRAM_OTOACOUSTIC_PRES]: AUDIOGRAM_OTOACOUSTIC_PRES,
  [AUDIOGRAM_OTOACOUSTIC_RED]: AUDIOGRAM_OTOACOUSTIC_RED
};

export const AUDIOGRAM_PURE_TONES = new EnumDataSource(audiogramPureToneValues);

export const OTOACOUSTIC_TONES = new EnumDataSource(otoacousticValues);

/** An OAE cell as the editor labels it: a code such as "A" reads "NR", a number passes through. */
export const formatOtoacousticReportValue = (value: string | null) =>
  OTOACOUSTIC_TONES.getFromCode(value) || value;

export const QUICK_SIN_CONFIGURATION_METHOD_LEFT_RIGHT = "left_and_right";
export const QUICK_SIN_CONFIGURATION_METHOD_BINAURAL = "binaural";

export const QUICK_SIN_CONFIGURATION_METHODS = new EnumDataSource({
  [QUICK_SIN_CONFIGURATION_METHOD_LEFT_RIGHT]: "Left and Right",
  [QUICK_SIN_CONFIGURATION_METHOD_BINAURAL]: "Binaural"
});
